import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Download, FileText, Loader2, Upload, X, Sparkles, AlertTriangle } from 'lucide-react';

const ROLE_STYLE = {
  broker: { bg: 'rgba(26,76,124,0.1)', color: '#1A4C7C', label: 'Broker' },
  client: { bg: 'rgba(29,158,117,0.1)', color: '#1D9E75', label: 'Client' },
  provider: { bg: 'rgba(232,160,32,0.12)', color: '#B27516', label: 'Provider' },
};

export const RecordDocuments = ({ recordType, recordId, compact = false, enableDrop = false }) => {
  const api = useApi();
  const [docs, setDocs] = useState(null);
  const [hasUploads, setHasUploads] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadRole, setUploadRole] = useState('broker');
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const fileRef = useRef(null);
  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  const load = useCallback(() => {
    api.get(`/documents/record/${recordType}/${recordId}`)
      .then(r => { setDocs(r.data.documents); setHasUploads(!!r.data.has_uploads); })
      .catch(err => { console.error('API error:', err?.message); setDocs([]); });
  }, [api, recordType, recordId]);

  useEffect(() => { load(); }, [load]);

  // Poll while any upload is still being analysed by AI
  useEffect(() => {
    if (!docs?.some(d => d.ai_summary?.status === 'processing')) return;
    const t = setTimeout(load, 4000);
    return () => clearTimeout(t);
  }, [docs, load]);

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true); setError('');
    const fd = new FormData();
    fd.append('file', file);
    fd.append('uploader_role', uploadRole);
    try {
      await api.post(`/documents/record/${recordType}/${recordId}/upload`, fd);
      load();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Upload failed');
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleUpload = (e) => uploadFile(e.target.files?.[0]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    uploadFile(e.dataTransfer?.files?.[0]);
  };

  const handleDelete = async (docId) => {
    try { await api.delete(`/documents/record/${recordType}/${recordId}/upload/${docId}`); load(); }
    catch (err) { setError(err?.response?.data?.detail || 'Delete failed'); }
  };

  if (docs === null) return <div className="flex items-center gap-2 py-3 text-xs" style={{ color: '#5C6B7A' }}><Loader2 className="w-3.5 h-3.5 animate-spin" />Loading documents...</div>;

  return (
    <div data-testid={`record-documents-${recordType}`}
      onDragOver={enableDrop ? (e) => { e.preventDefault(); setDragActive(true); } : undefined}
      onDragLeave={enableDrop ? () => setDragActive(false) : undefined}
      onDrop={enableDrop ? handleDrop : undefined}>
      {enableDrop && (
        <div data-testid="record-doc-dropzone"
          onClick={() => fileRef.current?.click()}
          className="flex flex-col items-center justify-center gap-1.5 py-6 mb-3 rounded-md cursor-pointer transition-colors"
          style={{
            border: `2px dashed ${dragActive ? '#FF9933' : 'rgba(136,150,168,0.35)'}`,
            background: dragActive ? 'rgba(255,153,51,0.06)' : 'rgba(245,247,250,0.6)',
          }}>
          {uploading ? <Loader2 className="w-5 h-5 animate-spin" style={{ color: '#FF9933' }} /> : <Upload className="w-5 h-5" style={{ color: dragActive ? '#FF9933' : '#5C6B7A' }} />}
          <p className="text-xs font-medium" style={{ color: dragActive ? '#D96E00' : '#0D1B2A' }}>
            {uploading ? 'Uploading…' : dragActive ? 'Drop to upload' : 'Drag & drop a document here, or click to browse'}
          </p>
          <p className="text-[10px]" style={{ color: '#64748B' }}>PDF, images, Word, Excel, CSV, email files · 10MB max · AI extraction runs automatically</p>
        </div>
      )}
      {docs.length === 0 && <p className="text-xs py-2" style={{ color: '#5C6B7A' }}>No documents on file.</p>}
      <div className="space-y-0">
        {docs.map(d => {
          const rs = ROLE_STYLE[d.uploader_role] || ROLE_STYLE.broker;
          const ai = d.ai_summary;
          return (
            <div key={d.doc_id} className="py-2 border-b" style={{ borderColor: 'rgba(136,150,168,0.08)' }} data-testid={`doc-row-${d.slug}`}>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5 min-w-0">
                <FileText className="w-4 h-4 shrink-0" style={{ color: d.source === 'uploaded' ? '#1D9E75' : '#5C6B7A' }} />
                <div className="min-w-0">
                  <p className="text-xs font-medium truncate" style={{ color: '#0D1B2A' }}>{d.name}</p>
                  {!compact && (
                    <p className="text-[10px]" style={{ color: '#5C6B7A' }}>
                      {d.uploaded_by} · {new Date(d.uploaded_at).toLocaleDateString('en-AU')} · {d.size_kb} KB
                      {d.source === 'uploaded' && <span className="ml-1.5 px-1 py-px rounded text-[8px] font-bold tracking-wider" style={{ background: 'rgba(29,158,117,0.12)', color: '#1D9E75' }}>UPLOADED</span>}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider" style={{ background: rs.bg, color: rs.color }}>{rs.label}</span>
                <a href={`${API_BASE}${d.download_url}`} target="_blank" rel="noreferrer"
                  className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium transition-colors hover:opacity-80"
                  style={{ background: 'rgba(26,76,124,0.08)', color: '#1A4C7C' }}
                  data-testid={`doc-download-${d.slug}`}>
                  <Download className="w-3 h-3" />Download
                </a>
                {d.source === 'uploaded' && (
                  <button onClick={() => handleDelete(d.doc_id)} title="Remove document"
                    className="p-1 rounded hover:bg-red-50 transition-colors" data-testid={`doc-delete-${d.doc_id}`}>
                    <X className="w-3 h-3" style={{ color: '#E24B4A' }} />
                  </button>
                )}
              </div>
            </div>
            {ai?.status === 'processing' && (
              <div className="flex items-center gap-1.5 mt-1.5 ml-6 text-[10px]" style={{ color: '#1A4C7C' }} data-testid={`ai-processing-${d.doc_id}`}>
                <Loader2 className="w-3 h-3 animate-spin" />AI reading document…
              </div>
            )}
            {ai?.status === 'complete' && (
              <div className="mt-1.5 ml-6 px-2.5 py-2 rounded-md" style={{ background: 'rgba(0,180,216,0.05)', border: '1px solid rgba(0,180,216,0.15)' }} data-testid={`ai-summary-${d.doc_id}`}>
                <div className="flex items-center gap-1.5 mb-1">
                  <Sparkles className="w-3 h-3" style={{ color: '#00B4D8' }} />
                  <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: '#0E7490' }}>AI Summary</span>
                  {ai.document_category && <span className="px-1.5 py-px rounded text-[8px] font-semibold uppercase" style={{ background: 'rgba(26,76,124,0.08)', color: '#1A4C7C' }}>{ai.document_category.replace(/_/g, ' ')}</span>}
                </div>
                <p className="text-[11px] leading-relaxed" style={{ color: '#0D1B2A' }}>{ai.summary}</p>
                {ai.key_facts?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {ai.key_facts.map((f, i) => (
                      <span key={i} className="px-1.5 py-0.5 rounded text-[9px]" style={{ background: '#FFFFFF', border: '1px solid rgba(136,150,168,0.2)', color: '#3A4A5A' }}>{f}</span>
                    ))}
                  </div>
                )}
                {ai.cross_check && ai.cross_check.status !== 'no_quantum' && (
                  <div className="flex items-start gap-1.5 mt-1.5 px-2 py-1.5 rounded" data-testid={`cross-check-${d.doc_id}`}
                    style={{ background: ai.cross_check.status === 'match' ? 'rgba(29,158,117,0.08)' : ai.cross_check.status === 'over_quantum' ? 'rgba(226,75,74,0.08)' : 'rgba(232,160,32,0.1)' }}>
                    <span className="text-[9px] font-bold uppercase tracking-wider shrink-0 mt-px"
                      style={{ color: ai.cross_check.status === 'match' ? '#1D9E75' : ai.cross_check.status === 'over_quantum' ? '#E24B4A' : '#B27516' }}>
                      {ai.cross_check.status === 'match' ? 'Quantum ✓' : 'Quantum ⚠'}
                    </span>
                    <span className="text-[10px]" style={{ color: '#3A4A5A' }}>{ai.cross_check.message}</span>
                  </div>
                )}
                {ai.flags?.length > 0 && (
                  <div className="flex items-start gap-1.5 mt-1.5">
                    <AlertTriangle className="w-3 h-3 mt-px shrink-0" style={{ color: '#E8A020' }} />
                    <span className="text-[10px]" style={{ color: '#B27516' }}>{ai.flags.join(' · ')}</span>
                  </div>
                )}
              </div>
            )}
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-2 mt-2.5 pt-1">
        <select value={uploadRole} onChange={e => setUploadRole(e.target.value)}
          className="px-2 py-1 text-[10px] rounded border" style={{ borderColor: 'rgba(136,150,168,0.25)', color: '#5C6B7A' }}
          data-testid="upload-role-select">
          <option value="broker">Broker</option>
          <option value="client">Client</option>
          <option value="provider">Provider</option>
        </select>
        <input ref={fileRef} type="file" onChange={handleUpload} className="hidden" id={`upload-${recordType}-${recordId}`}
          accept=".pdf,.png,.jpg,.jpeg,.docx,.xlsx,.csv,.txt,.eml,.msg" data-testid="upload-file-input" />
        <button onClick={() => fileRef.current?.click()} disabled={uploading}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-medium transition-colors hover:opacity-80 disabled:opacity-50"
          style={{ background: 'rgba(29,158,117,0.1)', color: '#1D9E75' }} data-testid="upload-doc-btn">
          {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
          {uploading ? 'Uploading…' : 'Upload Document'}
        </button>
        {!hasUploads && docs.length > 0 && <span className="text-[9px]" style={{ color: '#64748B' }}>Uploading a real file replaces the generated set</span>}
        {error && <span className="text-[10px]" style={{ color: '#E24B4A' }} data-testid="upload-error">{error}</span>}
      </div>
    </div>
  );
};

export default RecordDocuments;
